import React, { useEffect, useState } from "react";
import { services, areasServed, testimonials, faqs, gallery } from "../mock";
import { BookingForm } from "../components/BookingForm";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Home as HomeIcon, Building2, Ruler, ClipboardCheck, Hammer, Sparkles, Star, Phone, Mail, MapPin } from "lucide-react";
import { Toaster } from "../components/ui/sonner";
import HowWeWork from "./HowWeWork";
import Process from "./Process";
import CostGuide from "./CostGuide";
import PlanningGuide from "./PlanningGuide";
import { SEO } from "../components/SEO";

const icons = { Home: HomeIcon, Building2, Ruler, ClipboardCheck, Hammer };
const LOGO_URL = "https://customer-assets.emergentagent.com/job_spacegrow/artifacts/7nerl75q_RB%20Colour%20RGB%20Renobuild%20Group%20logo.png";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 14);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const headerClasses = `sticky top-0 z-40 backdrop-blur-xl border-b transition-[background-color,backdrop-filter] duration-500 ease-out ${
    isScrolled ? "bg-white/70 border-white/30" : "bg-[hsl(22,100%,96%)] border-white/20"
  }`;
  const logoSize = isScrolled ? "h-8 md:h-10" : "h-12 md:h-16";
  const paddingY = isScrolled ? "py-2" : "py-3";
  const shadow = isScrolled ? "shadow-[0_10px_30px_-20px_rgba(0,0,0,0.25)]" : "";

  const scrollToBooking = () => {
    const el = document.getElementById("booking");
    el?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header className={`${headerClasses} ${shadow}`}>
      <div className={`max-w-6xl mx-auto px-6 ${paddingY} flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Renobuild Group logo" className={`${logoSize} w-auto object-contain`} />
          <span className="font-semibold tracking-tight">Renobuild Group</span>
        </div>
        <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
          <a href="#services" className="hover:text-foreground transition-colors">Services</a>
          <a href="#work" className="hover:text-foreground transition-colors">Work</a>
          <a href="#how-we-work" className="hover:text-foreground transition-colors">How we work</a>
          <a href="#process" className="hover:text-foreground transition-colors">Process</a>
          <a href="#costs" className="hover:text-foreground transition-colors">Costs</a>
          <a href="#planning" className="hover:text-foreground transition-colors">Planning</a>
          <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
          <a href="#contact" className="hover:text-foreground transition-colors">Contact</a>
        </nav>
        <Button onClick={scrollToBooking} className="bg-[hsl(22,100%,50%)] hover:bg-[hsl(22,100%,42%)] text-white">Book Home Visit</Button>
      </div>
    </header>
  );
};

const TestimonialCard = ({ t }) => (
  <Card className="bg-white/70 backdrop-blur-md border-white/40">
    <CardContent className="pt-6">
      <div className="flex items-center gap-1 mb-2">
        {Array.from({ length: t.rating }).map((_, i) => (
          <Star key={i} className="h-4 w-4 text-[hsl(22,100%,50%)] fill-[hsl(22,100%,50%)]" />
        ))}
      </div>
      <p className="text-sm leading-relaxed">{t.quote}</p>
      <p className="text-xs text-muted-foreground mt-3">{t.name}</p>
    </CardContent>
  </Card>
);

export default function Home() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "HomeAndConstructionBusiness",
      "name": "Renobuild Group",
      "image": LOGO_URL,
      "url": typeof window !== 'undefined' ? window.location.origin : undefined,
      "telephone": "+44 7791 112996",
      "email": "hello@renobuildgroup.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "5 Brayford Square",
        "addressLocality": "London",
        "postalCode": "E1 0SG",
        "addressCountry": "GB"
      },
      "areaServed": ["London", "Sussex", "Surrey", "Kent"],
      "priceRange": "£££"
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Renobuild Group",
      "url": typeof window !== 'undefined' ? window.location.origin : undefined
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Home Extensions in London | Renobuild Group"
        description="Specialists in single and double-storey extensions, loft conversions and kitchen extensions across London & the Home Counties. Book a free home visit."
        schema={schema}
      />
      <Toaster richColors />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-10 -left-10 h-64 w-64 rounded-full bg-[hsl(22,100%,85%)] blur-3xl" />
          <div className="absolute top-20 right-0 h-72 w-72 rounded-full bg-pink-200/40 blur-3xl" />
        </div>
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24 grid md:grid-cols-2 gap-10">
          <div className="flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 text-[hsl(22,100%,35%)] bg-[hsl(22,100%,95%)] border border-[hsl(22,100%,85%)] rounded-full px-3 py-1 w-fit text-xs">
              <Sparkles className="h-4 w-4" />
              Specialists in home extensions
            </div>
            <h1 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tight">
              Design & build extensions that feel made for your home
            </h1>
            <p className="mt-4 text-muted-foreground max-w-prose">
              From planning to handover, Renobuild Group delivers high-quality single and double-storey extensions across London & the Home Counties.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Badge variant="secondary" className="bg-white/70">Planning support</Badge>
              <Badge variant="secondary" className="bg-white/70">Fixed schedule</Badge>
              <Badge variant="secondary" className="bg-white/70">Insured & accredited</Badge>
            </div>
            <div className="mt-8">
              <a href="#booking">
                <Button className="bg-[hsl(22,100%,50%)] hover:bg-[hsl(22,100%,42%)] text-white">Book a Free Home Visit</Button>
              </a>
            </div>
          </div>

          <div id="booking" className="">
            <BookingForm />
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="max-w-6xl mx-auto px-6 py-16">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">What we can build for you</h2>
          <p className="text-muted-foreground">End-to-end extensions with tidy sites, clear comms, and excellent finishes.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => {
            const Icon = icons[s.icon] || HomeIcon;
            return (
              <Card key={s.id} className="group border-white/40 bg-white/70 backdrop-blur-md transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl">
                <CardHeader>
                  <div className="h-10 w-10 rounded-md bg-[hsl(22,100%,50%)] text-white flex items-center justify-center shadow-md">
                    <Icon className="h-5 w-5" />
                  </div>
                  <CardTitle className="mt-2">{s.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{s.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Work / Gallery */}
      <section id="work" className="max-w-6xl mx-auto px-6 py-16">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Recent work</h2>
            <p className="text-muted-foreground">A small selection of completed projects.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {gallery.map((g) => (
            <Card key={g.id} className="overflow-hidden border-white/40 bg-white/70 backdrop-blur-md transition-transform duration-300 hover:-translate-y-1">
              <img src={g.src} alt={g.title} className="h-48 w-full object-cover" />
              <CardContent className="p-4">
                <p className="text-sm font-medium">{g.title}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <HowWeWork />
      <Process />
      <CostGuide />
      <PlanningGuide />

      {/* Testimonials */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Clients say</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <TestimonialCard key={i} t={t} />)
          )}
        </div>
      </section>

      {/* Areas served */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="rounded-xl border border-white/20 bg-white/50 backdrop-blur-md p-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <MapPin className="h-4 w-4" /> We cover London & the Home Counties
          </div>
          <div className="flex flex-wrap gap-2">
            {areasServed.map((a) => (
              <Badge key={a} variant="secondary" className="bg-white/80">{a}</Badge>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-6xl mx_auto px-6 py-16">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">FAQ</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f, idx) => (
            <AccordionItem key={idx} value={`item-${idx}`} className="border border-white/30 bg-white/60 backdrop-blur-md rounded-lg px-4">
              <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Contact CTA (visually distinct) */}
      <section id="contact" className="max-w-6xl mx-auto px-6 py-16">
        <div className="rounded-xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 bg-[hsl(22,100%,50%)] text-white shadow-[0_10px_50px_-20px_rgba(0,0,0,0.4)]">
          <div>
            <h3 className="text-xl font-semibold">Ready to discuss your extension?</h3>
            <p className="opacity-90">Book a visit or email us and we’ll get back within 24 hours.</p>
          </div>
          <div className="flex items-center gap-3">
            <a href="#booking"><Button className="bg-white text-[hsl(22,100%,35%)] hover:bg-amber-50">Book visit</Button></a>
            <a href="mailto:hello@renobuildgroup.com"><Button variant="outline" className="border-white text-white hover:bg-white/10"><Mail className="h-4 w-4 mr-2" />hello@renobuildgroup.com</Button></a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/30 bg_white/60 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-muted-foreground flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Renobuild Group logo small" className="h-6 w-auto object-contain" />
            Renobuild Group © {new Date().getFullYear()}
          </div>
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6 text-left md:text-right">
            <span className="flex items-center gap-2"><Phone className="h-4 w-4" /> 07791112996</span>
            <span className="flex items-center gap-2"><Mail className="h-4 w-4" /> hello@renobuildgroup.com</span>
            <span>5 Brayford Square, London, E1 0SG</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
